type DataLayerEvent = {
  event: string;
  [key: string]: unknown; // Allow other dynamic properties
};

declare global {
  interface Window {
    dataLayer: DataLayerEvent[]; // Array of specific event objects
  }
}
export const pushToDataLayer = (eventName: string, eventData = {}) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      ...eventData,
    });
  } else {
    console.warn('GTM dataLayer is not initialized.');
  }
};

export const toGa4Item = (
  product: any,
  index?: number,
  fallbackCategory?: string,
  quantity = 1
) => {
  const category = product?.category?.name ??
    (typeof product?.category === 'string' ? product.category : undefined) ??
    product?.category_name ??
    product?.product_category?.name ??
    product?.product_category ??
    product?.categories?.[0]?.name ??
    fallbackCategory;

  return {
    item_id: String(product?.product_id ?? product?.id ?? product?.sku ?? ''),
    item_name: product?.name ?? product?.product_name ?? product?.title ?? '',
    price: Number(product?.sale_price ?? product?.price ?? 0),
    ...(category ? { item_category: category } : {}),
    ...(product?.sku ? { item_variant: String(product.sku) } : {}),
    ...(index !== undefined ? { index } : {}),
    quantity: Number(quantity),
  };
};

export const toGa4CartItem = (item: any, index?: number) =>
  toGa4Item(
    { ...item, sale_price: undefined },
    index,
    undefined,
    Number(item?.quantity) || 1
  );

export const pushPromotionEvent = (
  eventName: 'view_promotion' | 'select_promotion',
  promotion: {
    id: string;
    name: string;
    creativeName: string;
    creativeSlot: string;
    items?: unknown[];
  }
) => {
  pushToDataLayer(eventName, {
    ecommerce: {
      creative_name: promotion.creativeName,
      creative_slot: promotion.creativeSlot,
      promotion_id: promotion.id,
      promotion_name: promotion.name,
      items: promotion.items ?? [],
    },
  });
};

type AuthEventName = 'login' | 'sign_up';
const PENDING_AUTH_EVENT_KEY = 'ga4_pending_auth_event';

export const rememberPendingAuthEvent = (
  event: AuthEventName,
  method: string
) => {
  if (typeof window === 'undefined') return;

  window.sessionStorage.setItem(
    PENDING_AUTH_EVENT_KEY,
    JSON.stringify({ event, method, createdAt: Date.now() })
  );
};

export const clearPendingAuthEvent = () => {
  if (typeof window !== 'undefined') {
    window.sessionStorage.removeItem(PENDING_AUTH_EVENT_KEY);
  }
};

export const trackPendingAuthEvent = () => {
  if (typeof window === 'undefined') return;

  const pendingEvent = window.sessionStorage.getItem(PENDING_AUTH_EVENT_KEY);
  clearPendingAuthEvent();
  if (!pendingEvent) return;

  try {
    const { event, method, createdAt } = JSON.parse(pendingEvent);
    const isValidEvent = event === 'login' || event === 'sign_up';
    const isRecent = Date.now() - Number(createdAt) < 10 * 60 * 1000;

    if (isValidEvent && method && isRecent) {
      pushToDataLayer(event, { method });
    }
  } catch {
    clearPendingAuthEvent();
  }
};
